import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import TextInput from '../../components/ui/TextInput.jsx';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { GOAL_TYPES, THRESHOLD_OPERATORS, thresholdConditionText } from '../../components/performance/goalTypes';

// P3 (PM analitik tamamlama, 2026-10-08) — yeni hedef tiplerinin (kilometre taşı, eşik/ikili)
// ortak görünümü. Hedeflerim (sahip), Ekip Hedefleri / Dönem Sonu (yönetici) ve salt-okunur
// detaylarda AYNI bileşen kullanılır:
//   mode="owner"   + canAct → bekleyen/reddedilen adımda "Tamamlandı Bildir" (kanıtla)
//   mode="manager" + canAct → kabul bekleyen adımda "Kabul Et" / "Reddet" (ret gerekçesi zorunlu)
//   mode="readonly"          → yalnız görünüm
// Hata tostu YALNIZ interceptor'da: çağrılar boolean döner, başarıda onChanged() tetiklenir.

// Sabitler tek kaynakta (components/performance/goalTypes.js); geriye uyum için yeniden dışa aktarılır.
export { GOAL_TYPES, THRESHOLD_OPERATORS, thresholdConditionText };

const Section = styled.div`
  margin: 12px 0;
`;

const SectionTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 16px 0 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.surface.border};
  padding-bottom: 6px;
`;

const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 8px;
`;

const Track = styled.div`
  flex: 1;
  height: 8px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.surface.border};
  overflow: hidden;
`;

const Fill = styled.div`
  height: 100%;
  width: ${({ $pct }) => Math.max(0, Math.min(100, Number($pct) || 0))}%;
  background: ${({ theme }) => theme.colors.secondary.main};
`;

const StepRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) 70px 110px minmax(0, 1fr);
  gap: 10px;
  align-items: start;
  background: ${({ theme }) => theme.colors.bgColor};
  border-radius: ${({ theme }) => theme.metrics.radius.large};
  padding: 10px 12px;
  margin-bottom: 8px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Sub = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: 2px;
  word-break: break-word;
`;

const toneColor = (theme, tone) => ({
  pending: theme.colors.text.muted,
  submitted: theme.colors.primary.main,
  accepted: theme.colors.secondary.dark,
  rejected: theme.colors.auxiliar.danger,
  met: theme.colors.secondary.dark,
  notMet: theme.colors.auxiliar.danger,
}[tone] || theme.colors.text.primary);

const Pill = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ theme }) => theme.colors.surface.card};
  border: 1px solid ${({ theme, $tone }) => toneColor(theme, $tone)};
  color: ${({ theme, $tone }) => toneColor(theme, $tone)};
  white-space: nowrap;
`;

const Actions = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const EvidenceLink = styled.a`
  color: ${({ theme }) => theme.colors.primary.main};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  word-break: break-all;
`;

const Value = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  padding: 4px 0;
`;

const dayOf = (v) => (v ? String(v).slice(0, 10) : '');

const emptyDialog = { kind: null, step: null };

function MilestoneDetails({ goal, mode, canAct, onSubmitStep, onDecideStep, onChanged }) {
  const { t } = useTranslation();
  const steps = goal.milestoneSteps || [];
  const [dialog, setDialog] = useState(emptyDialog);
  const [note, setNote] = useState('');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);

  const open = (kind, step) => { setNote(''); setUrl(''); setDialog({ kind, step }); };
  const close = () => setDialog(emptyDialog);

  const confirm = useCallback(async () => {
    if (!dialog.step) return;
    setBusy(true);
    let ok = false;
    if (dialog.kind === 'submit') {
      ok = await onSubmitStep(goal.id, dialog.step.id, {
        evidenceNote: note.trim() || undefined,
        evidenceUrl: url.trim() || undefined,
      });
    } else {
      ok = await onDecideStep(goal.id, dialog.step.id, dialog.kind === 'accept' ? 'accept' : 'reject', note.trim() || undefined);
    }
    setBusy(false);
    if (ok) {
      close();
      onChanged?.();
    }
  }, [dialog, note, url, goal.id, onSubmitStep, onDecideStep, onChanged]);

  // Kanıt: not ve/veya bağlantı (en az biri); ret: gerekçe zorunlu.
  const confirmDisabled = (dialog.kind === 'submit' && !note.trim() && !url.trim())
    || (dialog.kind === 'reject' && !note.trim());

  const accepted = steps.filter((s) => s.status === 'accepted').length;
  const pct = goal.milestoneProgressPct ?? 0;

  return (
    <Section data-role="milestone-details">
      <SectionTitle>{t('performance.goal.milestone.steps')}</SectionTitle>
      <ProgressRow>
        <Track><Fill $pct={pct} /></Track>
        <span data-role="milestone-progress">{`${pct}%`}</span>
        <Sub>{t('performance.goal.milestone.acceptedOf', { accepted, total: steps.length })}</Sub>
      </ProgressRow>
      {steps.length === 0 && <Value>{t('performance.goal.milestone.noSteps')}</Value>}
      {steps.map((s) => (
        <StepRow key={s.id} data-role={`step-row-${s.id}`}>
          <div>
            <div>{s.title}</div>
            {s.evidenceNote && <Sub>{`${t('performance.goal.milestone.evidence')}: ${s.evidenceNote}`}</Sub>}
            {s.evidenceUrl && (
              <EvidenceLink href={s.evidenceUrl} target="_blank" rel="noopener noreferrer">{s.evidenceUrl}</EvidenceLink>
            )}
            {s.decisionNote && <Sub>{`${t('performance.goal.milestone.decisionNote')}: ${s.decisionNote}`}</Sub>}
            {s.decidedByName && s.status === 'accepted' && (
              <Sub>{t('performance.goal.milestone.acceptedBy', { name: s.decidedByName, date: dayOf(s.decidedAt) })}</Sub>
            )}
          </div>
          <div>{`${s.weightPct}%`}</div>
          <div>
            <Pill $tone={s.status}>{t(`performance.goal.milestone.status.${s.status}`, { defaultValue: s.status })}</Pill>
            {s.dueDate && <Sub>{dayOf(s.dueDate)}</Sub>}
          </div>
          <Actions>
            {mode === 'owner' && canAct && (s.status === 'pending' || s.status === 'rejected') && (
              <Button variant="outline" onClick={() => open('submit', s)} data-action={`submit-step-${s.id}`}>
                {t('performance.goal.milestone.markDone')}
              </Button>
            )}
            {mode === 'manager' && canAct && s.status === 'submitted' && (
              <>
                <Button onClick={() => open('accept', s)} data-action={`accept-step-${s.id}`}>
                  {t('performance.goal.milestone.accept')}
                </Button>
                <Button variant="outline" onClick={() => open('reject', s)} data-action={`reject-step-${s.id}`}>
                  {t('performance.goal.milestone.reject')}
                </Button>
              </>
            )}
          </Actions>
        </StepRow>
      ))}

      <ConfirmDialog
        isOpen={!!dialog.kind}
        type={dialog.kind === 'reject' ? 'danger' : 'primary'}
        title={dialog.kind ? t(`performance.goal.milestone.dialog.${dialog.kind}Title`) : ''}
        message={dialog.step ? `${dialog.step.title} (${dialog.step.weightPct}%)` : ''}
        confirmText={dialog.kind ? t(`performance.goal.milestone.dialog.${dialog.kind}Confirm`) : undefined}
        onConfirm={confirm}
        onCancel={close}
        loading={busy}
        confirmDisabled={confirmDisabled}
      >
        {dialog.kind === 'submit' && (
          <>
            <TextInput
              label={t('performance.goal.milestone.evidenceNote')}
              type="textarea"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <TextInput
              label={t('performance.goal.milestone.evidenceUrl')}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
            <Sub>{t('performance.goal.milestone.evidenceHint')}</Sub>
          </>
        )}
        {(dialog.kind === 'accept' || dialog.kind === 'reject') && (
          <>
            {dialog.step?.evidenceNote && <Sub>{`${t('performance.goal.milestone.evidence')}: ${dialog.step.evidenceNote}`}</Sub>}
            {dialog.step?.evidenceUrl && (
              <EvidenceLink href={dialog.step.evidenceUrl} target="_blank" rel="noopener noreferrer">{dialog.step.evidenceUrl}</EvidenceLink>
            )}
            <TextInput
              label={dialog.kind === 'reject' ? t('performance.goal.milestone.rejectNote') : t('performance.goal.milestone.acceptNote')}
              type="textarea"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              required={dialog.kind === 'reject'}
            />
          </>
        )}
      </ConfirmDialog>
    </Section>
  );
}

function ThresholdDetails({ goal }) {
  const { t } = useTranslation();
  const met = goal.thresholdConditionMet;
  const resultTone = met == null ? 'pending' : met ? 'met' : 'notMet';
  const resultText = met == null
    ? t('performance.goal.threshold.noResult')
    : met ? t('performance.goal.threshold.met') : t('performance.goal.threshold.notMet');
  const actual = goal.thresholdOperator === 'binary'
    ? (goal.thresholdMet == null ? '-' : t(goal.thresholdMet ? 'performance.goal.threshold.yes' : 'performance.goal.threshold.no'))
    : (goal.thresholdActualValue ?? '-');
  return (
    <Section data-role="threshold-details">
      <SectionTitle>{t('performance.goal.threshold.condition')}</SectionTitle>
      <Value>{thresholdConditionText(t, goal)}</Value>
      <Value>
        {`${t('performance.goal.threshold.actual')}: ${actual} `}
        <Pill $tone={resultTone} data-role="threshold-result">{resultText}</Pill>
      </Value>
      {goal.thresholdVersion > 1 && (
        <Sub>{t('performance.goal.threshold.versionInfo', { version: goal.thresholdVersion })}</Sub>
      )}
    </Section>
  );
}

export default function GoalTypeDetails({
  goal, mode = 'readonly', canAct = false, onSubmitStep, onDecideStep, onChanged,
}) {
  if (!goal) return null;
  if (goal.goalType === 'milestone') {
    return (
      <MilestoneDetails
        goal={goal}
        mode={mode}
        canAct={canAct}
        onSubmitStep={onSubmitStep}
        onDecideStep={onDecideStep}
        onChanged={onChanged}
      />
    );
  }
  if (goal.goalType === 'threshold') return <ThresholdDetails goal={goal} />;
  return null;
}
